import { Box, HStack, Stack, Text } from '@chakra-ui/react'
import { markdownLookBack } from '@llm-ui/markdown'
import { useLLMOutput } from '@llm-ui/react'
import { format } from 'date-fns'
import type { FC } from 'react'

import { Avatar } from '@app/components/ui/avatar'
import useGetObject from '@app/hooks/useGetObject'
import MessageContent from '@app/pages/strategies/components/aiChat/components/messageContent'
import type { DomainChat, DomainMessage } from '@app/types'
import profileImage from '@app/utils/profileImage'
import { ChatProcessedStateEnum } from '@graphql/types'

type Props = Pick<DomainMessage, 'role'> & {
  chat: DomainChat
  messages: [string, DomainMessage][]
}

const MessageGroup: FC<Props> = ({ chat, role, messages }) => {
  const messageData = messages[0][1]
  const propUser = useGetObject(messageData.userId, 'user')
  const user = messageData.user || propUser

  const timestamp = messages[messages.length - 1][1].updatedAt
  const dateTime = timestamp ? format(timestamp, 'MMMM do, yyy h:mm aaa') : null

  const { blockMatches } = useLLMOutput({
    llmOutput: messageData.content,
    fallbackBlock: {
      component: MessageContent,
      lookBack: markdownLookBack()
    },
    blocks: [],
    isStreamFinished: chat?.processedState === ChatProcessedStateEnum.Idle
  })

  return (
    <HStack align="flex-start" gap="5">
      <Box pt="1">
        {role === 'assistant' ? (
          <Avatar bg="blue.500" name="Artificial Intelligence" size="sm" />
        ) : (
          <Avatar name={user?.name} size="sm" src={profileImage(user?.email)} />
        )}
      </Box>

      <Stack gap="1">
        <Text fontWeight="medium">
          {role === 'assistant' ? messageData.assistant?.name || 'Assistant' : user?.name}
        </Text>
        <Stack gap="2">
          {blockMatches.map((blockMatch, index) => {
            const Component = blockMatch.block.component

            // eslint-disable-next-line react/no-array-index-key
            return <Component key={index} blockMatch={blockMatch} />
          })}
        </Stack>
        {dateTime && (
          <Text color="fg.subtle" fontSize="xs">
            {dateTime}
          </Text>
        )}
      </Stack>
    </HStack>
  )
}

export default MessageGroup
